import Card from "@ciq/shared/components/card"
import { EntryInput } from "@ciq/shared/components/modal"
import { Select } from "@ciq/shared/components/select"
import { useCallback, useEffect, useMemo, useReducer, useState } from "react"
import { Button } from "@ciq/shared/components/buttons"
import Api from "@ciq/shared/api/api"
import { ErrorToast, SuccessToast } from "@ciq/shared/utils/toast"
import Indicator from "@ciq/shared/components/loader"
import { Tabs, TabHeader } from "@ciq/shared/components/tabs"
import { JobEnableSection } from "./components/JobEnableSection"
import { JobHistoryTab } from "./jobHistory/JobHistoryTab"

const TAB_ENABLE_JOBS = "enable-jobs"
const TAB_RUN_JOB = "run-job"
const TAB_JOB_HISTORY = "job-history"

const SPARK_TABS = [
  { id: TAB_ENABLE_JOBS, label: "Enable Jobs" },
  { id: TAB_RUN_JOB, label: "Run Job" },
  { id: TAB_JOB_HISTORY, label: "Job History" },
]

// NOTE: dependsOn fields must stay in sync with JOB_DEPENDENCIES in JobEnableSection.js
const JobOptions = {
  WIFI_SESSION: { label: "Wifi Session", value: "wifi_sessions", alwaysEnabled: true },
  STUDENT: { label: "Student", value: "wifi_student_parser", settingKey: "allow_student_job" },
  FACILITY_USAGE: { label: "Facility Usage", value: "facility_usage", settingKey: "allow_facility_usage_job" },
  CLASS_ATTENDANCE: { label: "Class Attendance", value: "class_attendance", settingKey: "allow_class_attendance_job", dependsOn: "allow_student_job" },
  WEEKLY_BEHAVIOR: { label: "Weekly Behavior", value: "derived_variable", settingKey: "allow_behavior_job", dependsOn: "allow_student_job" },
  EVENT_ATTENDANCE: { label: "Event Attendance", value: "event_attendance", settingKey: "allow_event_attendance_job" },
}

const DEFAULT_SETTINGS = {
  allow_student_job: false,
  allow_class_attendance_job: false,
  allow_behavior_job: false,
  allow_facility_usage_job: false,
  allow_event_attendance_job: false,
}

const DEFAULT_DATA = {
  loading: false,
  settingsLoading: false,
  job: null,
  start: null,
  end: null,
  settings: DEFAULT_SETTINGS,
  pendingSettings: DEFAULT_SETTINGS,
}

const SET_LOADING = "SET_LOADING"
const SET_SETTINGS_LOADING = "SET_SETTINGS_LOADING"
const SET_VALUE = "SET_VALUE"
const SET_SETTINGS = "SET_SETTINGS"
const UPDATE_PENDING_SETTING = "UPDATE_PENDING_SETTING"

const SparkViewReducer = (state = DEFAULT_DATA, action) => {
  switch (action.type) {
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      }

    case SET_SETTINGS_LOADING:
      return {
        ...state,
        settingsLoading: action.payload,
      }

    case SET_VALUE:
      return {
        ...state,
        [action.key]: action.payload,
      }

    case SET_SETTINGS:
      return {
        ...state,
        settingsLoading: false,
        settings: action.payload,
        pendingSettings: action.payload,
      }

    case UPDATE_PENDING_SETTING:
      return {
        ...state,
        pendingSettings: {
          ...state.pendingSettings,
          [action.key]: action.value,
        },
      }

    default:
      throw new Error(`Unknown action type: ${action.type}`)
  }
}

export function SparkView({ school }) {
  const [state, dispatch] = useReducer(SparkViewReducer, DEFAULT_DATA)
  const [selectedTab, setSelectedTab] = useState(TAB_RUN_JOB)

  const setLoading = useCallback(loading => dispatch({ type: SET_LOADING, payload: loading }), [])
  const setSettingsLoading = useCallback(loading => dispatch({ type: SET_SETTINGS_LOADING, payload: loading }), [])

  const handleOnJobChange = useCallback(job => dispatch({ type: SET_VALUE, key: "job", payload: job }), [])
  const handleOnStartChange = useCallback(start => dispatch({ type: SET_VALUE, key: "start", payload: start }), [])
  const handleOnEndChange = useCallback(end => dispatch({ type: SET_VALUE, key: "end", payload: end }), [])

  // Filter job options based on enabled settings and mark dependency-blocked options as disabled
  const enabledJobOptions = useMemo(() => {
    return Object.entries(JobOptions)
      .filter(([, opt]) => {
        if (opt.alwaysEnabled) return true
        if (!state.settings[opt.settingKey]) return false  // Job itself is disabled — hide it
        return true  // Job is enabled — show it (may be disabled due to dependency)
      })
      .map(([key, opt]) => {
        const isDependencyUnmet = opt.dependsOn && !state.settings[opt.dependsOn]
        return { ...opt, key, isDisabled: isDependencyUnmet }
      })
  }, [state.settings])

  // Clear selected job if it's no longer enabled or its dependency is unmet
  useEffect(() => {
    if (!state.job) return
    const selectedOption = enabledJobOptions.find(opt => opt.value === state.job.value)
    if (!selectedOption || selectedOption.isDisabled) {
      dispatch({ type: SET_VALUE, key: "job", payload: null })
    }
  }, [state.job, enabledJobOptions])

  // Fetch settings on mount
  useEffect(() => {
    if (!school?.id) {
      return
    }

    const controller = new AbortController()
    setSettingsLoading(true)

    Api.get(`schools/${school.id}/spark/settings`, { signal: controller.signal })
      .then(response => {
        const settings = response.data?.settings
        if (!settings || typeof settings !== "object") {
          console.error("Invalid settings response:", response.data)
          ErrorToast("Invalid settings response from server")
          setSettingsLoading(false)
          return
        }
        dispatch({ type: SET_SETTINGS, payload: settings })
      })
      .catch(e => {
        // Skip state updates for aborted requests to prevent race conditions
        if (e.error?.code === "ERR_CANCELED") return
        setSettingsLoading(false)
        if (e.handled) return
        console.error(e.error ? e.error : e)
        ErrorToast("Unable to fetch Spark settings")
      })

    return () => controller.abort()
  }, [school?.id, setSettingsLoading])

  const handleSettingChange = useCallback((key, value) => {
    dispatch({ type: UPDATE_PENDING_SETTING, key, value })
  }, [])

  const handleSaveSettings = useCallback(() => {
    if (!school?.id) return

    // Only send the 5 job flags - is_live_stream is auto-managed by backend
    const { allow_student_job, allow_class_attendance_job, allow_behavior_job, allow_facility_usage_job, allow_event_attendance_job } =
      state.pendingSettings
    const payload = { allow_student_job, allow_class_attendance_job, allow_behavior_job, allow_facility_usage_job, allow_event_attendance_job }

    setSettingsLoading(true)
    Api.put(`schools/${school.id}/spark/settings`, payload)
      .then(response => {
        const settings = response.data?.settings
        if (!settings || typeof settings !== "object") {
          console.error("Invalid settings response:", response.data)
          ErrorToast("Invalid settings response from server")
          setSettingsLoading(false)
          return
        }
        dispatch({ type: SET_SETTINGS, payload: settings })
        SuccessToast("Spark settings updated successfully.")
      })
      .catch(e => {
        setSettingsLoading(false)
        if (e.handled) return
        console.error(e.error ? e.error : e)
        // Surface server validation messages (e.g., dependency errors)
        const errorMessage = e.error?.message || "Unable to update Spark settings"
        ErrorToast(errorMessage)
      })
  }, [school?.id, state.pendingSettings, setSettingsLoading])

  const handleOnRun = useCallback(() => {
    if (!school?.id) {
      return
    }

    setLoading(true)
    Api.post(`schools/${school?.id}/spark`, { job: state.job.value, start: state.start, end: state.end })
      .then(() => {
        SuccessToast("Job started successfully.")
        setLoading(false)
      })
      .catch(e => {
        setLoading(false)

        if (e.handled) {
          return
        }

        console.log(e.error ? e.error : e)
        ErrorToast("Unable to start job")
      })
  }, [school?.id, setLoading, state])

  const handleOnSemester = useCallback(() => {
    if (!school?.id) {
      return
    }

    setLoading(true)
    Api.post(`schools/${school?.id}/spark/semester`, {})
      .then(() => {
        SuccessToast("Job started successfully.")
        setLoading(false)
      })
      .catch(e => {
        setLoading(false)

        if (e.handled) {
          return
        }

        console.log(e.error ? e.error : e)
        ErrorToast("Unable to start job")
      })
  }, [school?.id, setLoading])

  const handleOnFacility = useCallback(() => {
    if (!school?.id) {
      return
    }

    setLoading(true)
    Api.post(`schools/${school?.id}/spark/hourly`, {})
      .then(() => {
        SuccessToast("Job started successfully.")
        setLoading(false)
      })
      .catch(e => {
        setLoading(false)

        if (e.handled) {
          return
        }

        console.log(e.error ? e.error : e)
        ErrorToast("Unable to start job")
      })
  }, [school?.id, setLoading])

  const isButtonDisabled = !school?.id || !state.job || !state.start

  const handleSelectTab = (tab) => setSelectedTab(tab.id)

  return (
    <Card className="spark-view">
      <h1 className="sr-only">Spark</h1>
      <Tabs>
        <TabHeader selectedTab={selectedTab} items={SPARK_TABS} onSelectTab={handleSelectTab} />

        <div className="spark-tab-content">
          {selectedTab === TAB_ENABLE_JOBS && (
            <div className="column-flex gap-400">
              <span className="spark-section-header">Enable Spark Jobs</span>
              <JobEnableSection
                settings={state.pendingSettings}
                onChange={handleSettingChange}
                onSave={handleSaveSettings}
                isLoading={state.settingsLoading}
              />
            </div>
          )}

          {selectedTab === TAB_RUN_JOB && (
            <>
              <div className="column-flex gap-400">
                <div className="spark-view-content">
                  <span className="spark-section-header">Run Job</span>
                  <Select
                    options={enabledJobOptions}
                    value={enabledJobOptions.find(opt => opt.value === state.job?.value) || null}
                    onChange={handleOnJobChange}
                    isOptionDisabled={(option) => option.isDisabled}
                    placeholder="Select a job..."
                    isClearable
                  />
                  <EntryInput label="Start Date" type="date" value={state.start} onChange={handleOnStartChange} />
                  <EntryInput label="End Date" type="date" value={state.end} onChange={handleOnEndChange} />
                </div>
                <div className="spark-view-buttons">
                  <Button variant="primary" disabled={isButtonDisabled} onClick={handleOnRun}>
                    Run
                  </Button>
                </div>
              </div>

              <div className="spark-view-content spark-map-datasets">
                <span className="spark-section-header">Map View Datasets</span>
                <div className="row-flex gap-200">
                  <Button className="flex-grow" variant="primary" onClick={handleOnSemester}>
                    Generate Semester Summary
                  </Button>
                  <Button className="flex-grow" variant="primary" onClick={handleOnFacility}>
                    Generate Facility Usage Hourly
                  </Button>
                </div>
              </div>
            </>
          )}

          {selectedTab === TAB_JOB_HISTORY && <JobHistoryTab />}
        </div>
      </Tabs>

      <Indicator show={state.loading || state.settingsLoading} />
    </Card>
  )
}
